import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Paper,
} from '@mui/material';
import { DataGrid, GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { Plan, CreatePlanRequest, UpdatePlanRequest } from '../../../shared/support/adminFbacTypes';
import { getPlans, createPlan, updatePlan, deletePlan } from '../services/adminFbacApi';

const PlanCatalogPage: React.FC = () => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<Plan | null>(null);
  const [formState, setFormState] = useState<Partial<CreatePlanRequest | UpdatePlanRequest>>({});

  const fetchPlans = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getPlans();
      setPlans(data);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch plans.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  const handleOpenDialog = (plan?: Plan) => {
    setEditingPlan(plan ?? null);
    setFormState(plan ? { friendlyName: plan.friendlyName, description: plan.description, isActive: plan.isActive } : { isActive: true });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingPlan(null);
    setFormState({});
  };

  const handleSave = async () => {
    try {
      if (editingPlan) {
        await updatePlan(editingPlan.planId, formState as UpdatePlanRequest);
      } else {
        await createPlan(formState as CreatePlanRequest);
      }
      handleCloseDialog();
      await fetchPlans();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to save plan.');
    }
  };

  const handleDelete = async (planId: string) => {
    if (window.confirm(`Are you sure you want to delete plan "${planId}"? This will also delete all associated feature mappings.`)) {
      try {
        await deletePlan(planId);
        await fetchPlans();
      } catch (err: any) {
        setError(err.message || 'Failed to delete plan.');
      }
    }
  };

  const columns: GridColDef[] = [
    { field: 'planId', headerName: 'Plan ID', flex: 1 },
    { field: 'friendlyName', headerName: 'Friendly Name', flex: 2 },
    { field: 'purchaserType', headerName: 'Purchaser', flex: 1 },
    { field: 'entitlementTargetType', headerName: 'Target', flex: 1 },
    { field: 'isActive', headerName: 'Active', type: 'boolean', width: 100 },
    {
      field: 'actions',
      type: 'actions',
      width: 100,
      getActions: (params) => [
        <GridActionsCellItem icon={<EditIcon />} label="Edit" onClick={() => handleOpenDialog(params.row as Plan)} />,
        <GridActionsCellItem icon={<DeleteIcon />} label="Delete" onClick={() => handleDelete(params.row.id as string)} />,
      ],
    },
  ];

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Plan Catalog</Typography>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
          Create Plan
        </Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Paper sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={plans}
          columns={columns}
          getRowId={(row) => row.planId}
          loading={loading}
          slots={{
            loadingOverlay: () => <CircularProgress />,
          }}
        />
      </Paper>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} fullWidth maxWidth="sm">
        <DialogTitle>{editingPlan ? 'Edit Plan' : 'Create New Plan'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Plan ID"
            fullWidth
            variant="outlined"
            value={editingPlan?.planId || formState.planId || ''}
            onChange={(e) => setFormState({ ...formState, planId: e.target.value })}
            disabled={!!editingPlan}
            helperText={!editingPlan ? "e.g., 'policy-deployment-wizard'. Cannot be changed later." : ''}
          />
          <TextField
            margin="dense"
            label="Friendly Name"
            fullWidth
            variant="outlined"
            value={formState.friendlyName || ''}
            onChange={(e) => setFormState({ ...formState, friendlyName: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={formState.description || ''}
            onChange={(e) => setFormState({ ...formState, description: e.target.value })}
          />
          {!editingPlan && (
            <>
              <FormControl fullWidth margin="dense">
                <InputLabel>Purchaser Type</InputLabel>
                <Select
                  value={formState.purchaserType || ''}
                  label="Purchaser Type"
                  onChange={(e) => setFormState({ ...formState, purchaserType: e.target.value as 'Customer' | 'MSP' })}
                >
                  <MenuItem value="Customer">Customer</MenuItem>
                  <MenuItem value="MSP">MSP</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth margin="dense">
                <InputLabel>Entitlement Target Type</InputLabel>
                <Select
                  value={formState.entitlementTargetType || ''}
                  label="Entitlement Target Type"
                  onChange={(e) => setFormState({ ...formState, entitlementTargetType: e.target.value as 'Customer' | 'MSP' })}
                >
                  <MenuItem value="Customer">Customer</MenuItem>
                  <MenuItem value="MSP">MSP</MenuItem>
                </Select>
              </FormControl>
            </>
          )}
          <FormControlLabel
            control={
              <Switch
                checked={formState.isActive ?? false}
                onChange={(e) => setFormState({ ...formState, isActive: e.target.checked })}
              />
            }
            label="Is Active"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PlanCatalogPage;